import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, PencilIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { TIMEZONES } from '../../constants';
import { scheduleApi } from './tutorApi';

// Helper function to format time for display
const formatTime = (time) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

// Helper function to convert 24-hour to 12-hour format
const convertTo12Hour = (time24) => {
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

// Helper function to convert 12-hour to 24-hour format
const convertTo24Hour = (time12) => {
  const [time, ampm] = time12.split(' ');
  const [hours, minutes] = time.split(':');
  let hour = parseInt(hours);
  
  if (ampm === 'PM' && hour !== 12) {
    hour += 12;
  } else if (ampm === 'AM' && hour === 12) {
    hour = 0;
  }
  
  return `${hour.toString().padStart(2, '0')}:${minutes}`;
};

export default function Schedule() {
  const [scheduleData, setScheduleData] = useState({
    timezone: 'Asia/Calcutta', // Default fallback
    schedules: []
  });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [newSlot, setNewSlot] = useState({
    startTime: '09:00',
    endTime: '10:00',
    isAvailable: true,
    isBooked: false,
    eventName: ''
  });
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [suggestionData, setSuggestionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Auto-detect timezone on component mount
  useEffect(() => {
    const detectTimezone = () => {
      try {
        const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        console.log(`System timezone detected: ${systemTimezone}`);
        console.log(`Available timezones include this one: ${TIMEZONES.includes(systemTimezone)}`);
        
        if (TIMEZONES.includes(systemTimezone)) {
          setScheduleData(prev => ({
            ...prev,
            timezone: systemTimezone
          }));
          console.log(`✅ Auto-detected timezone: ${systemTimezone}`);
        } else {
          console.log(`❌ System timezone ${systemTimezone} not found in our list, using default: Asia/Calcutta`);
        }
      } catch (error) {
        console.log('❌ Error detecting timezone, using default: Asia/Calcutta', error);
      }
    };

    detectTimezone();
  }, []);

  // Create stable month key for dependency
  const monthKey = `${currentMonth.getFullYear()}-${currentMonth.getMonth()}`;
  
  // Load schedule data on component mount and when month changes
  useEffect(() => {
    loadScheduleData();
  }, [monthKey]);


  // Load schedule data from API
  const loadScheduleData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1; // JavaScript months are 0-indexed
      
      const data = await scheduleApi.getSchedule(year, month);
      
      // Auto-detect timezone if not set or if it's the default
      let timezone = data.timezone || 'Asia/Calcutta';
      
      if (!timezone || timezone === 'Asia/Calcutta') {
        try {
          const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (TIMEZONES.includes(systemTimezone)) {
            timezone = systemTimezone;
          }
        } catch (error) {
          // Use default timezone
        }
      }
      
      // Only update schedule data, don't reset modal state
      setScheduleData(prev => ({
        ...prev,
        timezone: timezone,
        schedules: data.schedules || []
      }));
    } catch (err) {
      console.error('Error loading schedule:', err);
      setError('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  };

  // Get date key for storage
  const getDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get schedule for a specific date
  const getScheduleForDate = (date) => {
    const dateKey = getDateKey(date);
    return scheduleData.schedules.find(schedule => schedule.date === dateKey);
  };

  // Get availability for a specific date
  const getAvailabilityForDate = (date) => {
    const schedule = getScheduleForDate(date);
    if (!schedule || schedule.is_unavailable) {
      return [];
    }
    return schedule.time_slots || [];
  };

  // Check if a date has availability
  const hasAvailabilityForDate = (date) => {
    return getAvailabilityForDate(date).length > 0;
  };

  // Check if a date is marked as unavailable
  const isDateUnavailable = (date) => {
    const schedule = getScheduleForDate(date);
    return schedule?.is_unavailable || false;
  };

  // Add a new time slot
  const handleAddSlot = async () => {
    if (newSlot.startTime && newSlot.endTime && selectedDate) {
      // newSlot.startTime and newSlot.endTime are already in 24-hour format (HH:MM) from the select dropdowns
      const start24 = newSlot.startTime;
      const end24 = newSlot.endTime;
      
      if (start24 >= end24) {
        alert('End time must be after start time');
        return;
      }

      try {
        setLoading(true);
        
        const dateKey = getDateKey(selectedDate);
        const existingSchedule = getScheduleForDate(selectedDate);
        
        const newSlotData = {
          start_time: start24,
          end_time: end24,
          is_available: newSlot.isAvailable,
          is_booked: newSlot.isBooked,
          event_name: newSlot.eventName || null
        };

        const scheduleData = {
          date: dateKey,
          is_unavailable: false,
          time_slots: [
            ...(existingSchedule?.time_slots || []),
            newSlotData
          ].sort((a, b) => a.start_time.localeCompare(b.start_time))
        };
        
        await scheduleApi.updateSchedule(dateKey, scheduleData);
        await loadScheduleData(); // Reload data from API
        
        // Show smart suggestion for similar days
        showSmartSuggestion(selectedDate, newSlotData);
        
        setNewSlot({ startTime: '09:00', endTime: '10:00', isAvailable: true, isBooked: false, eventName: '' });
        setShowAddSlot(false);
      } catch (err) {
        console.error('Error adding slot:', err);
        alert('Failed to add time slot. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Edit a time slot
  const handleEditSlot = (slot, date) => {
    setEditingSlot(slot);
    setSelectedDate(date);
    
    // Ensure time is in 24-hour format (HH:MM) for the select dropdowns
    // The API returns time as "HH:MM:SS" or "HH:MM", we need just "HH:MM"
    const formatTimeForSelect = (time) => {
      if (!time) return '00:00';
      // If time is in "HH:MM:SS" format, extract just "HH:MM"
      if (time.includes(':')) {
        const parts = time.split(':');
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
      }
      return time;
    };
    
    setNewSlot({
      startTime: formatTimeForSelect(slot.start_time),
      endTime: formatTimeForSelect(slot.end_time),
      isAvailable: slot.is_available !== undefined ? slot.is_available : true,
      isBooked: slot.is_booked || false,
      eventName: slot.event_name || ''
    });
    setShowAddSlot(true);
  };

  // Update an existing slot
  const handleUpdateSlot = async () => {
    if (editingSlot && newSlot.startTime && newSlot.endTime && selectedDate) {
      // newSlot.startTime and newSlot.endTime are already in 24-hour format (HH:MM) from the select dropdowns
      const start24 = newSlot.startTime;
      const end24 = newSlot.endTime;
      
      if (start24 >= end24) {
        alert('End time must be after start time');
        return;
      }

      try {
        setLoading(true);
        
        const dateKey = getDateKey(selectedDate);
        const existingSchedule = getScheduleForDate(selectedDate);
        
        const updatedSchedule = {
          date: dateKey,
          is_unavailable: false,
          time_slots: (existingSchedule?.time_slots || []).map(slot =>
            slot.id === editingSlot.id
              ? { ...slot, start_time: start24, end_time: end24, is_available: newSlot.isAvailable, is_booked: newSlot.isBooked, event_name: newSlot.eventName || null }
              : slot
          ).sort((a, b) => a.start_time.localeCompare(b.start_time))
        };
        
        await scheduleApi.updateSchedule(dateKey, updatedSchedule);
        await loadScheduleData(); // Reload data from API
        
        setEditingSlot(null);
        setNewSlot({ startTime: '09:00', endTime: '10:00', isAvailable: true, isBooked: false, eventName: '' });
        setShowAddSlot(false);
      } catch (err) {
        console.error('Error updating slot:', err);
        alert('Failed to update time slot. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Delete a time slot
  const handleDeleteSlot = async (slotId, date) => {
    if (window.confirm('Are you sure you want to delete this time slot?')) {
      try {
        setLoading(true);
        
        const dateKey = getDateKey(date);
        const existingSchedule = getScheduleForDate(date);
        
        const updatedSchedule = {
          date: dateKey,
          is_unavailable: false,
          time_slots: (existingSchedule?.time_slots || []).filter(slot => slot.id !== slotId)
        };
        
        await scheduleApi.updateSchedule(dateKey, updatedSchedule);
        await loadScheduleData(); // Reload data from API
      } catch (err) {
        console.error('Error deleting slot:', err);
        alert('Failed to delete time slot. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Update timezone
  const handleTimezoneChange = (timezone) => {
    setScheduleData(prev => ({
      ...prev,
      timezone: timezone
    }));
    // Note: Timezone is stored in the tutor profile, not in schedule data
    // This is just for display purposes
  };

  // Cancel editing
  const handleCancel = () => {
    setShowAddSlot(false);
    setEditingSlot(null);
    setNewSlot({ startTime: '09:00', endTime: '10:00', isAvailable: true, isBooked: false });
    setSelectedDate(null);
  };

  // Show smart suggestion for similar days
  const showSmartSuggestion = (date, slotData) => {
    const dayOfWeek = date.getDay();
    const currentMonth = date.getMonth();
    const currentYear = date.getFullYear();
    
    // Find other days of the same weekday in the current month
    const similarDays = [];
    for (let day = 1; day <= 31; day++) {
      const checkDate = new Date(currentYear, currentMonth, day);
      if (checkDate.getMonth() === currentMonth && 
          checkDate.getDay() === dayOfWeek && 
          checkDate.getTime() !== date.getTime()) {
        const dateKey = getDateKey(checkDate);
        const daySchedule = getScheduleForDate(checkDate);
        if (!daySchedule || daySchedule.time_slots.length === 0) {
          similarDays.push(checkDate);
        }
      }
    }
    
    if (similarDays.length > 0) {
      setSuggestionData({
        slot: slotData,
        similarDays: similarDays,
        dayName: date.toLocaleDateString('en-US', { weekday: 'long' })
      });
      setShowSuggestion(true);
    }
  };

  // Apply suggestion to similar days
  const applySuggestion = async () => {
    if (suggestionData) {
      try {
        setLoading(true);
        
        // Apply the slot to each similar day
        for (const date of suggestionData.similarDays) {
          const dateKey = getDateKey(date);
          const existingSchedule = getScheduleForDate(date);
          
          const newSlotData = {
            start_time: suggestionData.slot.start_time,
            end_time: suggestionData.slot.end_time,
            is_available: suggestionData.slot.is_available,
            is_booked: suggestionData.slot.is_booked
          };

          const scheduleData = {
            date: dateKey,
            is_unavailable: false,
            time_slots: [
              ...(existingSchedule?.time_slots || []),
              newSlotData
            ].sort((a, b) => a.start_time.localeCompare(b.start_time))
          };
          
          await scheduleApi.updateSchedule(dateKey, scheduleData);
        }
        
        await loadScheduleData(); // Reload data from API
        setShowSuggestion(false);
        setSuggestionData(null);
        
        alert(`Successfully added time slots to ${suggestionData.similarDays.length} days!`);
      } catch (err) {
        console.error('Error applying suggestion:', err);
        alert('Failed to apply suggestion. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Copy last week's schedule
  const copyLastWeek = async () => {
    try {
      setLoading(true);
      
      const response = await scheduleApi.copyLastWeek();
      await loadScheduleData(); // Reload data from API
      
      alert(`Successfully copied ${response.data.copied_days} days from last week!`);
    } catch (err) {
      console.error('Error copying last week:', err);
      alert('Failed to copy last week\'s schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Generate time options for dropdowns
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const time12 = convertTo12Hour(time24);
        options.push({ value: time24, label: time12 });
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  // Handle clicking on a date
  const handleDateClick = (date) => {
    setSelectedDate(date);
    
    // Check if this date already has slots or is marked unavailable
    const daySchedule = getScheduleForDate(date);
    
    if (daySchedule && daySchedule.time_slots && daySchedule.time_slots.length > 0) {
      // Date has slots - show them for editing
      setShowAddSlot(true);
    } else if (daySchedule && daySchedule.is_unavailable) {
      // Date is marked unavailable - show modal to change
      setShowAddSlot(true);
    } else {
      // Empty date - show add modal
      setShowAddSlot(true);
    }
  };

  // Mark date as unavailable
  const markDateUnavailable = async (date) => {
    try {
      setLoading(true);
      
      const dateKey = getDateKey(date);
      const scheduleData = {
        date: dateKey,
        is_unavailable: true,
        time_slots: []
      };
      
      await scheduleApi.updateSchedule(dateKey, scheduleData);
      await loadScheduleData(); // Reload data from API
    } catch (err) {
      console.error('Error marking date unavailable:', err);
      alert('Failed to mark date as unavailable. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle marking date as unavailable from modal
  const handleMarkUnavailable = async () => {
    if (selectedDate) {
      await markDateUnavailable(selectedDate);
      setShowAddSlot(false);
      setSelectedDate(null);
    }
  };

  // Get existing slots for a date
  const getExistingSlots = (date) => {
    const schedule = getScheduleForDate(date);
    return schedule?.time_slots || [];
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Schedule Management</h1>
        <p className="mt-2 text-sm text-gray-600">
          Click on any date to set your availability. Students and staff will be able to see when you're available for tutoring.
        </p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <span className="text-sm text-blue-700">Loading schedule data...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center">
            <span className="text-sm text-red-700">{error}</span>
            <button
              onClick={loadScheduleData}
              className="ml-4 px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Timezone Selection */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Your Timezone</h2>
        <div className="max-w-md">
          <select
            value={scheduleData.timezone}
            onChange={(e) => handleTimezoneChange(e.target.value)}
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          All times below are shown in your selected timezone: <strong>{scheduleData.timezone}</strong>
          {scheduleData.timezone === Intl.DateTimeFormat().resolvedOptions().timeZone && 
            <span className="text-green-600 ml-1">(Auto-detected from your system)</span>
          }
        </p>
      </div>

      {/* Copy Last Week Button */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Quick Actions</h2>
            <p className="mt-1 text-sm text-gray-500">
              Copy your last week's schedule to this week for consistent availability.
            </p>
          </div>
          <button
            onClick={copyLastWeek}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
            Copy Last Week
          </button>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white shadow rounded-lg p-6">
        {/* Calendar Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-gray-900">
            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => setCurrentMonth(new Date())}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
            >
              Today
            </button>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Day headers */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
              {day}
            </div>
          ))}
          
          {/* Calendar days */}
          {getDaysInMonth(currentMonth).map((date, index) => {
            if (!date) {
              return <div key={index} className="p-2"></div>;
            }
            
            
            const hasAvailability = hasAvailabilityForDate(date);
            const availability = getAvailabilityForDate(date);
            const isToday = date.toDateString() === new Date().toDateString();
            const isUnavailable = isDateUnavailable(date);
            
            return (
              <div
                key={date.toISOString()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDateClick(date);
                }}
                className={`p-2 min-h-[100px] border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                  isToday ? 'bg-indigo-50 border-indigo-200' : 'bg-white'
                } ${isUnavailable ? 'bg-red-50 border-red-200' : ''}`}
                style={{ pointerEvents: 'auto' }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-medium ${
                    isToday ? 'text-indigo-600' : isUnavailable ? 'text-red-600' : 'text-gray-900'
                  }`}>
                    {date.getDate()}
                  </span>
                  <div className="flex items-center space-x-1">
                    {hasAvailability && !isUnavailable && (
                      <div className="w-2 h-2 bg-green-500 rounded-full" title="Available"></div>
                    )}
                    {isUnavailable && (
                      <div className="w-2 h-2 bg-red-500 rounded-full" title="Unavailable"></div>
                    )}
                    {!hasAvailability && !isUnavailable && (
                      <div className="w-2 h-2 bg-gray-200 rounded-full" title="No availability set"></div>
                    )}
                  </div>
                </div>
                
                {hasAvailability && !isUnavailable && (
                  <div className="space-y-1">
                    {availability.slice(0, 3).map((slot, slotIndex) => (
                      <div key={slotIndex} className={`text-xs truncate ${
                        !slot.is_available ? 'text-red-600 font-medium' :
                        slot.is_booked ? 'text-blue-600 font-medium' : 'text-gray-600'
                      }`}>
                        {formatTime(slot.start_time)}-{formatTime(slot.end_time)}
                        {!slot.is_available ? ' (Unavailable)' :
                         slot.is_booked && (
                          slot.event_name ? ` (${slot.event_name})` : ' (Booked)'
                        )}
                      </div>
                    ))}
                    {availability.length > 3 && (
                      <div className="text-xs text-gray-400">
                        +{availability.length - 3} more
                      </div>
                    )}
                  </div>
                )}
                
                {isUnavailable && (
                  <div className="text-xs text-red-600 font-medium">
                    Day Off
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white shadow rounded-lg p-6 mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Legend</h3>
        <div className="flex items-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span>Available slots</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span>Booked slots</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span>Unavailable slots / Day off</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-gray-200 rounded-full"></div>
            <span>No availability set</span>
          </div>
        </div>
        <p className="mt-3 text-sm text-gray-500">
          Click on any date to add or edit availability.
        </p>
      </div>

      {/* Add/Edit Time Slot Modal */}
      {showAddSlot && selectedDate && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          style={{ zIndex: 9999 }}
        >
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingSlot ? 'Edit Time Slot' : `Manage Availability - ${selectedDate.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric' 
                })}`}
              </h3>
              
              {/* Show existing slots if any */}
              {getExistingSlots(selectedDate).length > 0 && !editingSlot && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Current Time Slots:</h4>
                  <div className="space-y-2">
                    {getExistingSlots(selectedDate).map((slot) => (
                      <div key={slot.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${
                            !slot.is_available ? 'bg-red-500' : 
                            slot.is_booked ? 'bg-blue-500' : 'bg-green-500'
                          }`}></div>
                          <span className="text-sm text-gray-900">
                            {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                          </span>
                          <span className={`text-xs ${
                            !slot.is_available ? 'text-red-600' :
                            slot.is_booked ? 'text-blue-600' : 'text-green-600'
                          }`}>
                            {!slot.is_available ? 'Unavailable' :
                             slot.is_booked ? (slot.event_name || 'Booked') : 'Available'}
                          </span>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleEditSlot(slot, selectedDate)}
                            className="p-1 text-gray-400 hover:text-indigo-600"
                          >
                            <PencilIcon className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => handleDeleteSlot(slot.id, selectedDate)}
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <TrashIcon className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show day off status */}
              {isDateUnavailable(selectedDate) && !editingSlot && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                    <span className="text-sm text-red-700 font-medium">This day is marked as a day off</span>
                  </div>
                </div>
              )}

              {/* Add new slot form */}
              {!editingSlot && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Start Time</label>
                    <select
                      value={newSlot.startTime}
                      onChange={(e) => setNewSlot({ ...newSlot, startTime: e.target.value })}
                      className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    >
                      {timeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">End Time</label>
                    <select
                      value={newSlot.endTime}
                      onChange={(e) => setNewSlot({ ...newSlot, endTime: e.target.value })}
                      className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    >
                      {timeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Slot Status</label>
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        onClick={() => setNewSlot({ ...newSlot, isAvailable: true, isBooked: false, eventName: '' })}
                        className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                          newSlot.isAvailable && !newSlot.isBooked
                            ? 'bg-green-100 text-green-700 border-green-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Available
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewSlot({ ...newSlot, isAvailable: true, isBooked: true })}
                        className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                          newSlot.isBooked && newSlot.isAvailable
                            ? 'bg-blue-100 text-blue-700 border-blue-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Booked
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewSlot({ ...newSlot, isAvailable: false, isBooked: false, eventName: '' })}
                        className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                          !newSlot.isAvailable && !newSlot.isBooked
                            ? 'bg-red-100 text-red-700 border-red-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Unavailable
                      </button>
                    </div>
                  </div>

                  {/* Event Name Field - Only show for booked slots */}
                  {newSlot.isBooked && newSlot.isAvailable && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Event Name (Optional)</label>
                      <input
                        type="text"
                        value={newSlot.eventName}
                        onChange={(e) => setNewSlot({ ...newSlot, eventName: e.target.value })}
                        placeholder="e.g., Math Tutoring Session, Student Meeting"
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Edit slot form */}
              {editingSlot && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Start Time</label>
                    <select
                      value={newSlot.startTime}
                      onChange={(e) => setNewSlot({ ...newSlot, startTime: e.target.value })}
                      className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    >
                      {timeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">End Time</label>
                    <select
                      value={newSlot.endTime}
                      onChange={(e) => setNewSlot({ ...newSlot, endTime: e.target.value })}
                      className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    >
                      {timeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Slot Status</label>
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        onClick={() => setNewSlot({ ...newSlot, isAvailable: true, isBooked: false, eventName: '' })}
                        className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                          newSlot.isAvailable && !newSlot.isBooked
                            ? 'bg-green-100 text-green-700 border-green-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Available
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewSlot({ ...newSlot, isAvailable: true, isBooked: true })}
                        className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                          newSlot.isBooked && newSlot.isAvailable
                            ? 'bg-blue-100 text-blue-700 border-blue-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Booked
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewSlot({ ...newSlot, isAvailable: false, isBooked: false, eventName: '' })}
                        className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                          !newSlot.isAvailable && !newSlot.isBooked
                            ? 'bg-red-100 text-red-700 border-red-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Unavailable
                      </button>
                    </div>
                  </div>

                  {/* Event Name Field - Only show for booked slots */}
                  {newSlot.isBooked && newSlot.isAvailable && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Event Name (Optional)</label>
                      <input
                        type="text"
                        value={newSlot.eventName}
                        onChange={(e) => setNewSlot({ ...newSlot, eventName: e.target.value })}
                        placeholder="e.g., Math Tutoring Session, Student Meeting"
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between mt-6">
                <div className="flex space-x-2">
                  {!editingSlot && (
                    <button
                      onClick={handleMarkUnavailable}
                      className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Day Off
                    </button>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  {!editingSlot && (
                    <button
                      onClick={handleAddSlot}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Add Slot
                    </button>
                  )}
                  {editingSlot && (
                    <button
                      onClick={handleUpdateSlot}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Update Slot
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Smart Suggestion Modal */}
      {showSuggestion && suggestionData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Smart Suggestion</h3>
              <p className="text-sm text-gray-600 mb-4">
                You added {formatTime(suggestionData.slot.start_time)}-{formatTime(suggestionData.slot.end_time)} for {suggestionData.dayName}.
                Would you like to add the same time slot to {suggestionData.similarDays.length} other {suggestionData.dayName}s this month?
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowSuggestion(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  No, thanks
                </button>
                <button
                  onClick={applySuggestion}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                >
                  Yes, apply to all
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
